import { CONFERENCE_ZONE_TYPE_ID } from "../../../../../features/Booking-Form/typings/place-and-zone-type-ids.constant";
import { IFloorPayload } from "./FloorPayload.type";
import {
  FloorPlanTandemLink,
  TandemCandidate,
  TandemEndpoint,
  TandemObjectKind
} from "./TandemLink.type";

export function tandemEndpointKey(endpoint: TandemEndpoint): string {
  return `${endpoint.kind}:${endpoint.inventoryId}`;
}

export function endpointsEqual(a: TandemEndpoint, b: TandemEndpoint): boolean {
  return a.kind === b.kind && a.inventoryId === b.inventoryId;
}

/**
 * Optional gate endpoint for one-way mode. Must be `a` or `b` when set.
 */
export function canonicalizeTandemLink(
  a: TandemEndpoint,
  b: TandemEndpoint,
  dominant: TandemEndpoint | null = null
): FloorPlanTandemLink | null {
  if (a.kind === b.kind && a.inventoryId === b.inventoryId) {
    return null;
  }

  if (dominant && !endpointsEqual(dominant, a) && !endpointsEqual(dominant, b)) {
    return null;
  }

  const aFirst = a.kind < b.kind || (a.kind === b.kind && a.inventoryId < b.inventoryId);
  const left = aFirst ? a : b;
  const right = aFirst ? b : a;
  const link: FloorPlanTandemLink = {
    leftKind: left.kind,
    leftInventoryId: left.inventoryId,
    rightKind: right.kind,
    rightInventoryId: right.inventoryId
  };

  if (dominant) {
    link.dominantKind = dominant.kind;
    link.dominantInventoryId = dominant.inventoryId;
  }

  return link;
}

export function linkInvolvesEndpoint(link: FloorPlanTandemLink, endpoint: TandemEndpoint): boolean {
  return (
    (link.leftKind === endpoint.kind && link.leftInventoryId === endpoint.inventoryId) ||
    (link.rightKind === endpoint.kind && link.rightInventoryId === endpoint.inventoryId)
  );
}

export function partnerOfLink(
  link: FloorPlanTandemLink,
  endpoint: TandemEndpoint
): TandemEndpoint | null {
  if (link.leftKind === endpoint.kind && link.leftInventoryId === endpoint.inventoryId) {
    return { kind: link.rightKind, inventoryId: link.rightInventoryId };
  }
  if (link.rightKind === endpoint.kind && link.rightInventoryId === endpoint.inventoryId) {
    return { kind: link.leftKind, inventoryId: link.leftInventoryId };
  }
  return null;
}

export function findLink(
  links: FloorPlanTandemLink[] | undefined,
  endpoint: TandemEndpoint
): FloorPlanTandemLink | null {
  return (links ?? []).find(link => linkInvolvesEndpoint(link, endpoint)) ?? null;
}

export function findPartner(
  links: FloorPlanTandemLink[] | undefined,
  endpoint: TandemEndpoint
): TandemEndpoint | null {
  const match = findLink(links, endpoint);
  if (!match) {
    return null;
  }
  return partnerOfLink(match, endpoint);
}

export function dominantOfLink(link: FloorPlanTandemLink): TandemEndpoint | null {
  if (link.dominantKind == null || link.dominantInventoryId == null) {
    return null;
  }
  return { kind: link.dominantKind, inventoryId: link.dominantInventoryId };
}

/**
 * Places on the floor plus conference zones, excluding `self`.
 */
export function listTandemCandidates(
  floorPlan: IFloorPayload,
  self: TandemEndpoint
): TandemCandidate[] {
  const places: TandemCandidate[] = (floorPlan.places ?? []).flatMap(place => {
    const inventoryId = place.inventory?.id ?? place.inventoryId;
    if (self.kind === TandemObjectKind.PLACE && inventoryId === self.inventoryId) {
      return [];
    }
    return [
      {
        kind: TandemObjectKind.PLACE,
        inventoryId,
        name: place.inventory?.name ?? String(inventoryId),
        typeName:
          place.inventory?.placeType?.name ??
          floorPlan.placeTypes?.find(type => type.id === place.inventory?.placeTypeId)?.name ??
          "workplace"
      }
    ];
  });

  const zones: TandemCandidate[] = (floorPlan.zones ?? []).flatMap(zone => {
    const zoneTypeId =
      zone.zoneTypeId ?? zone.inventory?.zoneTypeId ?? zone.inventory?.zoneType?.id;
    if (zoneTypeId !== CONFERENCE_ZONE_TYPE_ID) {
      return [];
    }
    const inventoryId = zone.inventory?.id ?? zone.inventoryId;
    if (self.kind === TandemObjectKind.ZONE && inventoryId === self.inventoryId) {
      return [];
    }
    return [
      {
        kind: TandemObjectKind.ZONE,
        inventoryId,
        name: zone.inventory?.name ?? String(inventoryId),
        typeName: "conferencezone"
      }
    ];
  });

  return [...places, ...zones];
}

export function findCandidate(
  candidates: TandemCandidate[],
  endpoint: TandemEndpoint | null
): TandemCandidate | null {
  if (!endpoint) {
    return null;
  }
  return (
    candidates.find(
      candidate =>
        candidate.kind === endpoint.kind && candidate.inventoryId === endpoint.inventoryId
    ) ?? null
  );
}

export type TandemStealNotice = {
  /** Object whose previous partner will be cleared. */
  displaced: TandemEndpoint;
  /** Previous partner that will become unwired. */
  previousPartner: TandemEndpoint;
};

export type ApplyTandemAssignmentOptions = {
  /** Gate endpoint for one-way mode; `null`/omit for equal Rule A. */
  dominant?: TandemEndpoint | null;
};

function dominantFieldsEqual(link: FloorPlanTandemLink, dominant: TandemEndpoint | null): boolean {
  const current = dominantOfLink(link);
  if (!current && !dominant) {
    return true;
  }
  if (!current || !dominant) {
    return false;
  }
  return endpointsEqual(current, dominant);
}

/**
 * Replaces the 1:1 wire for `self`. Returns steal notices when an existing
 * wire must be removed (self already linked, or the chosen partner already linked).
 */
export function applyTandemAssignment(
  links: FloorPlanTandemLink[] | undefined,
  self: TandemEndpoint,
  partner: TandemEndpoint | null,
  options: ApplyTandemAssignmentOptions = {}
): { nextLinks: FloorPlanTandemLink[]; steals: TandemStealNotice[] } {
  const current = links ?? [];
  const steals: TandemStealNotice[] = [];
  const dominant = options.dominant ?? null;

  const currentPartner = findPartner(current, self);
  const existingLink = findLink(current, self);
  if (
    currentPartner &&
    partner &&
    endpointsEqual(currentPartner, partner) &&
    existingLink &&
    dominantFieldsEqual(existingLink, dominant)
  ) {
    return { nextLinks: current, steals: [] };
  }

  if (currentPartner && (!partner || !endpointsEqual(currentPartner, partner))) {
    steals.push({ displaced: self, previousPartner: currentPartner });
  }

  if (partner) {
    const partnerCurrent = findPartner(current, partner);
    if (partnerCurrent && !endpointsEqual(partnerCurrent, self)) {
      steals.push({ displaced: partner, previousPartner: partnerCurrent });
    }
  }

  const withoutInvolved = current.filter(link => {
    if (linkInvolvesEndpoint(link, self)) {
      return false;
    }
    if (partner && linkInvolvesEndpoint(link, partner)) {
      return false;
    }
    return true;
  });

  if (!partner) {
    return { nextLinks: withoutInvolved, steals };
  }

  const canonical = canonicalizeTandemLink(self, partner, dominant);
  if (!canonical) {
    return { nextLinks: withoutInvolved, steals };
  }

  return { nextLinks: [...withoutInvolved, canonical], steals };
}

export function candidateLabel(candidate: TandemCandidate): string {
  return `${candidate.name} (${candidate.typeName})`;
}

export function tandemPartnerName(
  floorPlan: IFloorPayload,
  self: TandemEndpoint
): string | undefined {
  const partner = findPartner(floorPlan.tandemLinks, self);
  const candidate = findCandidate(listTandemCandidates(floorPlan, self), partner);
  return candidate?.name;
}
